import React, { useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Speed,
  LocalGasStation,
  DriveEta,
  AirlineSeatReclineNormal,
  Luggage,
  Build,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useQuery } from 'react-query';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { vehicleApi } from '../services/api';
import { APP_DATE_FORMAT, formatCurrency } from '../utils/formatters';
import { useTranslation } from 'react-i18next';
import { fuelTypeLabel, statusLabel, vehicleClassLabel, vehicleTypeLabel } from '../i18n/translate';
import { APP_ROUTES, withSearch } from '../routing/appRoutes';

export const VehicleDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const action = searchParams.get('action');
  const [showReservationForm, setShowReservationForm] = useState(action === 'reserve');
  
  // Get dates from URL params if available
  const urlStartDate = searchParams.get('startDate');
  const urlEndDate = searchParams.get('endDate');
  
  // Reservation form state - pre-populate with URL params if available
  const [startDate, setStartDate] = useState<Dayjs | null>(
    urlStartDate ? dayjs(urlStartDate) : null
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    urlEndDate ? dayjs(urlEndDate) : null
  );
  const [pickupLocation, setPickupLocation] = useState('');
  const [dropoffLocation, setDropoffLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch vehicle details
  const { data: vehicle, isLoading, error: fetchError } = useQuery(
    ['vehicle', id],
    () => vehicleApi.getById(id!),
    { enabled: !!id }
  );

  // Calculate total cost
  const calculateTotalCost = () => {
    if (!startDate || !endDate || !vehicle) return 0;
    const days = endDate.diff(startDate, 'day');
    const rate = typeof vehicle.dailyRate === 'string' ? parseFloat(vehicle.dailyRate) : vehicle.dailyRate;
    return days * rate;
  };

  const handleReserve = () => {
    // No authentication required - go directly to reservation form
    setShowReservationForm(true);
  };

  const handleSubmitReservation = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!startDate || !endDate) {
      setError(t('vehicleDetails.validation.dates'));
      return;
    }

    if (!pickupLocation) {
      setError(t('vehicleDetails.validation.pickupLocation'));
      return;
    }

    const params = new URLSearchParams();
    params.set('vehicleId', id!);
    params.set('startDate', startDate.format('YYYY-MM-DD'));
    params.set('endDate', endDate.format('YYYY-MM-DD'));
    params.set('pickupLocation', pickupLocation);
    params.set('returnLocation', dropoffLocation || pickupLocation);
    if (notes.trim()) {
      params.set('specialRequests', notes.trim());
    }

    navigate(withSearch(APP_ROUTES.public.search, params));
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError || !vehicle) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{t('vehicleDetails.notFound')}</Alert>
        <Button onClick={() => navigate(APP_ROUTES.public.search)} sx={{ mt: 2 }}>
          {t('common.backToSearch')}
        </Button>
      </Container>
    );
  }

  const features = [
    { icon: <AirlineSeatReclineNormal />, label: t('vehicleDetails.seats'), value: vehicle.seatingCapacity },
    { icon: <Luggage />, label: t('vehicleDetails.luggage'), value: vehicle.luggageCapacity || t('common.unavailable') },
    { icon: <Speed />, label: t('fields.transmission'), value: vehicle.transmission },
    { icon: <LocalGasStation />, label: t('fields.fuel'), value: fuelTypeLabel(t, vehicle.fuelType) },
    { icon: <Build />, label: t('fields.engineSize'), value: vehicle.engineSize || t('common.unavailable') },
    { icon: <DriveEta />, label: t('fields.type'), value: vehicleTypeLabel(t, vehicle.type) },
    { icon: <DriveEta />, label: t('fields.vehicleClass'), value: vehicleClassLabel(t, vehicle.vehicleClass) },
  ];

  const amenities = [
    { key: 'airConditioning', label: t('vehicleDetails.airConditioning'), available: vehicle.airConditioning },
    { key: 'gpsNavigation', label: t('vehicleDetails.gpsNavigation'), available: vehicle.gpsNavigation },
    { key: 'bluetooth', label: t('vehicleDetails.bluetooth'), available: vehicle.bluetooth },
    { key: 'usbCharging', label: t('vehicleDetails.usbCharging'), available: vehicle.usbCharging },
    { key: 'backupCamera', label: t('vehicleDetails.backupCamera'), available: vehicle.backupCamera },
    { key: 'parkingSensors', label: t('vehicleDetails.parkingSensors'), available: vehicle.parkingSensors },
    { key: 'sunroof', label: t('vehicleDetails.sunroof'), available: vehicle.sunroof },
    { key: 'leatherSeats', label: t('vehicleDetails.leatherSeats'), available: vehicle.leatherSeats },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button onClick={() => navigate(APP_ROUTES.public.search)} sx={{ mb: 2 }}>
          {t('common.backToSearch')}
        </Button>

        <Grid container spacing={4}>
          {/* Left Column - Vehicle Info */}
          <Grid item xs={12} md={8}>
            {/* Vehicle Images */}
            <Paper sx={{ p: 0, mb: 3, overflow: 'hidden' }}>
              {vehicle.imageUrls && vehicle.imageUrls.length > 0 ? (
                <Box
                  component="img"
                  src={vehicle.imageUrls[0]}
                  alt={`${vehicle.make} ${vehicle.model}`}
                  sx={{ width: '100%', height: 400, objectFit: 'cover' }}
                  onError={(e) => {
                    // Fallback if image fails to load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: 400,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'grey.200',
                  }}
                >
                  <DriveEta sx={{ fontSize: 100, color: 'grey.400' }} />
                </Box>
              )}
            </Paper>

            {/* Vehicle Details */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h4" gutterBottom>
                {vehicle.make} {vehicle.model} ({vehicle.year})
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Chip label={vehicleTypeLabel(t, vehicle.type)} color="primary" />
                <Chip label={vehicleClassLabel(t, vehicle.vehicleClass)} variant="outlined" />
                <Chip label={statusLabel(t, vehicle.status)} color="success" />
                {vehicle.isFeatured && <Chip label={t('fleet.publicSearchBadge')} color="warning" />}
              </Box>

              <Typography variant="body1" color="text.secondary" paragraph>
                {vehicle.description || t('vehicleDetails.defaultDescription')}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* Key Features Grid */}
              <Typography variant="h6" gutterBottom>
                {t('vehicleDetails.keyFeatures')}
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {features.map((feature, index) => (
                  <Grid item xs={6} sm={4} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {feature.icon}
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {feature.label}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {feature.value}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Amenities */}
              <Typography variant="h6" gutterBottom>
                {t('vehicleDetails.amenities')}
              </Typography>
              <Grid container spacing={1}>
                {amenities.map((amenity) => (
                  <Grid item xs={6} sm={4} key={amenity.key}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {amenity.available ? (
                        <CheckCircle color="success" fontSize="small" />
                      ) : (
                        <Cancel color="disabled" fontSize="small" />
                      )}
                      <Typography
                        variant="body2"
                        color={amenity.available ? 'text.primary' : 'text.secondary'}
                      >
                        {amenity.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Right Column - Pricing & Reservation */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h3" color="primary">
                  {formatCurrency(vehicle.dailyRate)}
                  <Typography component="span" variant="h6" color="text.secondary">
                    /{t('common.day')}
                  </Typography>
                </Typography>
              </Box>

              {!showReservationForm ? (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleReserve}
                    sx={{ mb: 2 }}
                  >
                    {t('vehicleDetails.reserveNow')}
                  </Button>
                  <Typography variant="caption" color="text.secondary" align="center" display="block">
                    {t('vehicleDetails.cancellation')}
                  </Typography>
                </>
              ) : (
                <Box component="form" onSubmit={handleSubmitReservation}>
                  <Typography variant="h6" gutterBottom>
                    {t('vehicleDetails.makeReservation')}
                  </Typography>

                  {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                  <DatePicker
                    label={t('fields.startDate')}
                    value={startDate}
                    onChange={(newDate) => {
                      setStartDate(newDate);
                      // Update URL to preserve date
                      const params = new URLSearchParams(searchParams.toString());
                      if (newDate) {
                        params.set('startDate', newDate.format('YYYY-MM-DD'));
                      } else {
                        params.delete('startDate');
                      }
                      navigate(withSearch(APP_ROUTES.public.vehicleDetails(id || ':id'), params), {
                        replace: true,
                      });
                    }}
                    minDate={dayjs()}
                    format={APP_DATE_FORMAT}
                    slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                  />

                  <DatePicker
                    label={t('fields.endDate')}
                    value={endDate}
                    onChange={(newDate) => {
                      setEndDate(newDate);
                      // Update URL to preserve date
                      const params = new URLSearchParams(searchParams.toString());
                      if (newDate) {
                        params.set('endDate', newDate.format('YYYY-MM-DD'));
                      } else {
                        params.delete('endDate');
                      }
                      navigate(withSearch(APP_ROUTES.public.vehicleDetails(id || ':id'), params), {
                        replace: true,
                      });
                    }}
                    minDate={startDate || dayjs()}
                    format={APP_DATE_FORMAT}
                    slotProps={{ textField: { fullWidth: true, margin: 'normal' } }}
                  />

                  <TextField
                    fullWidth
                    margin="normal"
                    label={t('fields.pickupLocation')}
                    value={pickupLocation}
                    onChange={(e) => setPickupLocation(e.target.value)}
                    required
                  />

                  <TextField
                    fullWidth
                    margin="normal"
                    label={t('fields.dropoffLocation')}
                    value={dropoffLocation}
                    onChange={(e) => setDropoffLocation(e.target.value)}
                    placeholder={t('fields.sameAsPickup')}
                  />

                  <TextField
                    fullWidth
                    margin="normal"
                    label={t('fields.specialRequestsOptional')}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    multiline
                    rows={3}
                  />

                  {startDate && endDate && (
                    <Box sx={{ my: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>{t('vehicleDetails.rentalPeriodLabel')}</strong> {t('common.daysCount', { count: endDate.diff(startDate, 'day') })}
                      </Typography>
                      <Typography variant="h6" color="primary">
                        {t('fields.total')}: {formatCurrency(calculateTotalCost())}
                      </Typography>
                    </Box>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    {t('vehicleDetails.continueBooking', 'Continue to Booking')}
                  </Button>

                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => setShowReservationForm(false)}
                    sx={{ mt: 1 }}
                  >{t('common.cancel')}</Button>
    </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </LocalizationProvider>
  );
};
